<template>
    <div class="app" ref="appHTML">
        <div class="debugButton" @click="toggleDebug()" v-if="isDevelopment">Debug</div>

        <div class="fullscreen-button" @click="toggleFullscreen()">
            <img v-show="isInFullscreen" :src="getMedia('icons/fullscreenClose.png')" alt="Exit Fullscreen">
            <img v-show="!isInFullscreen" :src="getMedia('icons/fullscreenOpen.png')" alt="Enter Fullscreen">
        </div>

        <div class="ui-level">
            <transition name="slide">
                <ProductInfos v-if="isInfoPanelOpen && focusedProduct" :product="focusedProduct"/>
            </transition>
            <NavigationTopLeft/>
        </div>

        <NavigationBottomRight/>
        <Tooltip/>

        <transition name="fade">
            <Tutorial v-show="isTutorialVisible"/>
        </transition>

        <transition name="fade">
            <Loadingscreen v-show="loadingscreenVisible"/>
        </transition>

        <Renderer v-if="dataLoaded"/>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue';
import { useStore } from '@/store';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import Loadingscreen from "@/components/ui/Loadingscreen.vue";
import NavigationTopLeft from "@/components/ui/NavigationTopLeft.vue";
import NavigationBottomRight from "@/components/ui/NavigationBottomRight.vue";
import ProductInfos from "@/components/ui/ProductInfos.vue";
import Tooltip from "@/components/ui/Tooltip.vue";
import Tutorial from "@/components/ui/tutorial/Tutorial.vue";

import Renderer from "@/components/babylonjs/Renderer.vue";

import regionLinks from "@/assets/versions/regionLinks.json";
import versions from "@/assets/versions/versions.json";

import { getMedia } from "@/util";

const store = useStore();
const { selectedProduct, activeLanguage, loadingscreenVisible, isInfoPanelOpen, isTutorialVisible } = storeToRefs(store);

const { setLocaleMessage, locale } = useI18n();

const isInFullscreen = ref(false);
const dataLoaded = ref(false);
const appHTML = useTemplateRef('appHTML');

const focusedProduct = computed(() => {
    return selectedProduct.value ? {
        _ID: selectedProduct.value.DO_NOT_CHANGE_ID,
        _segment: selectedProduct.value.DO_NOT_CHANGE_segment,
        data: selectedProduct.value.data.find(data => data.lang === activeLanguage.value)
    } : null;
});

const isDevelopment = computed(() => {
    // Check URL params if dev = true
    let urlParams = new URLSearchParams(window.location.search);
    if(urlParams.get("dev") === "true") 
        return true;
    return process.env.NODE_ENV == 'development';
});

store.setIsMobile(screen.width < 1000);

async function loadData() {
    await Promise.all([
        loadProductData(),
        loadTranslationData()
    ]);

    dataLoaded.value = true;
    console.log("All Data loaded.");
}

async function loadProductData() {
    console.log("-- Loading Product-Data --");

    let originalURL = new URL(window.location.href);
    let search_params = originalURL.searchParams;

    let showroomConfigURL = regionLinks.regions.find(data => data.id == "DE")!.url;
    if(isDevelopment.value)
        showroomConfigURL = getMedia("versions/devShowroomConfig.json");
    
    // if region is specified in URL params, use that region's product.json
    if (search_params.get("region")) {
        let regionName = search_params.get("region")!
        const region = regionLinks.regions.find(data => data.id.toLowerCase() == regionName.toLowerCase());
        console.log("Region in parameter. Trying to find Region " + regionName + " in region list " + regionLinks.regions.map(r => r.id).join(", "));
        if (region) {
            showroomConfigURL = region.url;
            console.log("Region in URL params: " + regionName + " found in region list.");
        }else{
            console.warn("Region in URL params not found in region list: " + regionName);
        }
    }

    console.log("Fetching Product data from url: " + showroomConfigURL);
    const requestHeaders = new Headers();
    requestHeaders.append('Accept', 'application/json, text/plain, */*');
    let data = await fetch(showroomConfigURL, {
        method: 'GET',
        headers: requestHeaders,
        mode: 'cors'
    });
    let json = await data.json();
    let showroomConfig = json as ShowroomConfig;
    
    store.setLanguages(showroomConfig.language.available);
    store.setAvailableSegments(showroomConfig.availableSegments);
    store.setProducts(showroomConfig.products);

    // Set default language from URL params or showroomConfig
    store.setActiveLanguage(showroomConfig.language.default);
    if (search_params.get("language")) {
        let language = search_params.get("language")!;
        console.log("Language in URL params: " + language + ". Checking if it is available in showroomConfig.languages: " + showroomConfig.language.available);
        if(showroomConfig.language.available.find((lang) => lang.toLowerCase() === language.toLowerCase())){
            store.setActiveLanguage(language.toLowerCase());
            console.log("Language in URL params set as active language: " + language);
        } else {
            console.warn("Language in URL params is not available in showroomConfig: " + language);
        }
    }
    locale.value = store.activeLanguage;

    let versionNumber = showroomConfig.modelVersion ? showroomConfig.modelVersion : "1.0.0";
    let versionData:VersionData|undefined = versions.find(data => data.id == versionNumber);
    if(!versionData){
        versionData = versions[versions.length - 1];
        console.warn("Version Data not found for version: " + versionNumber + ". Using latest version data.");
    }
    store.setVersionData(versionData);
    console.log("Using Version Data: ", versionData);

    console.log("-- Product-Data loaded --");
}

const loadTranslationData = async () => {
    console.log("-- Loading Translation-Data --");

    let translationsUrl = "https://www.aliaxis.de/website/aliaxis/showroom/system.json";
    if(isDevelopment.value)
        translationsUrl = getMedia("versions/devTranslations.json");
    
    console.log("Fetching Translation data from url: " + translationsUrl);
    const requestHeaders = new Headers();
    requestHeaders.append('Accept', 'application/json, text/plain, */*');
    let data = await fetch(translationsUrl, {
        method: 'GET',
        headers: requestHeaders,
        mode: 'cors'
    });
    let translationsJSON = await data.json();
    
    translationsJSON.possibleLanguages.forEach((language:string) => {
        const tmp = {};
        Object.entries(translationsJSON.translations).forEach(([key, values]) => {
            // @ts-ignore
            tmp[key] = values[language];
        });
        
        setLocaleMessage(language, tmp);
    });

    console.log("-- Translation-Data loaded --");
};

const toggleFullscreen = () => {
    isInFullscreen.value = !isInFullscreen.value;
    isInFullscreen.value ? appHTML.value!.requestFullscreen() : document.exitFullscreen();
};

const toggleDebug = async () => {
    let { ShowInspector } = await import('@babylonjs/inspector');
    console.log(ShowInspector);
    ShowInspector(window.scene);
};

loadData();
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Questrial&display=swap');

:root {
    --main-orange: #32AAFF;
    --color-blue: #32AAFF;
    --color-blue-hover: #1f55d1;
    --color-lightblue: #32AAFF;
    --color-red: #FA6157;
    --color-orange: #FAA64A;
    --color-turquoise: #52DEAD;
    --color-green: #57DE52;
    --color-purple: #D645CC;
    --color-black: #5E5E5E;

    --combi-color-blue: var(--color-lightblue);
    --combi-color-red: var(--color-purple);
    --combi-color-orange: var(--color-red);
    --combi-color-turquoise: var(--color-green);

    --primaryColor: var(--color-lightblue);
    --md-theme-default-primary: var(--color-blue);
    
    --bg-dark: #5E5E5E;
    --bg-dark-hover: #6d6d6d; 
    --bg-light: rgb(250,250,250);
    --bg-light-hover: rgb(240,240,240);

    --font-family: 'Gilroy', Arial, sans-serif;
}

html, body, #app, .app{
    margin: 0;
    height: 100%;
    width: 100%;
    overflow: hidden;
    font-family: var(--font-family);
    font-size: 12.8px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    text-size-adjust: 100%;
}

.fullscreen-button{
    position: absolute;
    right: 40px;
    top: 40px;
    background-color: var(--color-blue);
    border-radius: 50%;
    width: 64px;
    height: 64px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
}
.fullscreen-button img{
    width: 50%;
    height: 50%;
    filter: invert(1);
}
.ui-level {
    position: absolute;
    z-index: 2;
    left: 0px;
    top: 0px;
    display: flex;
    align-items: flex-start;
    transition: left .5s ease;
    pointer-events: none;
}
.debugButton{
    position: absolute;
    left: calc(50% - 60px);
    top: 20px;
    padding: 10px 40px;
    background: var(--color-blue);
    transition: all .3s ease;
    color: white;
    cursor: pointer;
    border-radius: 5px;
}
.debugButton:hover{
    background: var(--color-blue-hover);
}
button{
    border: none;
    cursor: pointer;
    transition-duration: 300ms;
    font-size: 1.25rem;
}
button.primary{
    font-weight: 600;
    background-color: var(--color-lightblue);
    padding: 0.8125rem 1.5rem;
    color: white;
}
button.primary:hover{
    background-color: #007dd5;
}
button.tertiary{
    font-weight: 600;
    background-color: transparent;
    padding: 0.8125rem 1.5rem;
    color: var(--bg-dark);
}
button.tertiary:hover{
    color: var(--color-lightblue);
}
button.fab{
    border-radius: 50%;
    display:flex;
    justify-content: center;
    align-items: center;
    border: none;
    background-color: var(--bg-light);
    transition: .3s ease;
}
button.fab:hover{
    background-color: var(--bg-light-hover);
}
button.fab.s{
    height: 24px;
    width: 24px;
}
button.fab.m{
    height: 64px;
    width: 64px;
}
button.fab.l{
    height: 40px;
    width: 40px;
}
button.fab.dark{
    background-color: var(--bg-dark);
}
button.fab.dark:hover{
    background-color: var(--bg-dark-hover);
}
button.fab:disabled{
    cursor: default;
    background-color: var(--bg-light);
    opacity: 0.2;
}
button.exit{
    position: absolute;
    top:0.5rem;
    right:0.5rem;
    z-index: 2;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.5s ease-out;
}
.slide-enter-from, .slide-leave-to {
  transform: translateX(-100%);
}

@media (width <= 1000px){
    .fullscreen-button{
        bottom: 24px;
        left: 24px;
        top: initial;
        right: initial;
    }
    button.fab.m, .fullscreen-button{
        height: 55px;
        width: 55px;
    }
}
</style>